// utils.js

/**
 * Pads a given ZIP code with leading zeros until it's 5 characters long.
 * @param {string|number} zip - The ZIP code to format.
 * @return {string} - Formatted ZIP code (e.g. '00501').
 */
export function formatZip(zip) {
  if (!zip) return '';
  const s = '00000' + zip;
  return s.substr(s.length - 5);
}

/**
 * Formats a US phone number string (digits only) into a common style: (555) 555-5555.
 * @param {string} phone - The phone string, digits only (e.g. '5551234567').
 * @return {string} - The formatted phone number or an empty string if invalid.
 */
export function formatPhoneNumber(phone) {
  const clean = ('' + phone).replace(/\D/g, '');
  const match = clean.match(/^(\d{3})(\d{3})(\d{4})$/);
  return match ? `(${match[1]}) ${match[2]}-${match[3]}` : '';
}

/**
 * Cleans a phone number string, removing all non-digit characters.
 * @param {string} phone - The phone string to clean (e.g. '(555) 123-4567').
 * @return {string} - The phone number with only digits (e.g. '5551234567').
 */
export function cleanPhoneNumber(phone) {
  return ('' + phone).replace(/\D/g, '');
}

/**
 * Sorts an array of date strings in ascending order (MM/DD/YYYY format).
 * @param {string[]} dateArray - Array of date strings (e.g., ['4/22/2025', '3/10/2025']).
 * @return {string[]} - Sorted array of date strings.
 */
export function sortDates(dateArray) {
  return dateArray.sort((a, b) => {
	return new Date(a).getTime() - new Date(b).getTime();
  });
}



export function formatDate(date) {
  var day = String(date.getDate());
  var month = String(date.getMonth() + 1);
  return month + "/" + day + "/" + date.getFullYear();
}

/**
 * Creates a "friendly" date format from a standard date string (e.g. MM/DD/YYYY).
 * Example output: "Tuesday, April 22"
 * @param {string} dateStr - A date string in MM/DD/YYYY format.
 * @return {string} - Human-readable date, or empty string if invalid.
 */
export function formatFriendlyDate(dateStr) {
  const [month, day, year] = dateStr.split('/');
  const dateObj = new Date(`${year}-${month}-${day}`);
  if (isNaN(dateObj.getTime())) return '';
  
  const options = { weekday: 'long', month: 'long', day: 'numeric' };
  return dateObj.toLocaleDateString('en-US', options);
}

/**
 * Smoothly scrolls the browser to a given element.
 * @param {HTMLElement} element - The DOM element to scroll to.
 * @param {number} offset - Additional offset from the element’s top.
 */
export function smoothScrollTo(element, offset = 0) {
  if (!element) return;
  const topPos = element.getBoundingClientRect().top + window.pageYOffset + offset;
  window.scrollTo({
	top: topPos,
	behavior: 'smooth',
  });
}

/**
 * Logs debug information only if in dev mode.
 * @param  {...any} args - Any items to log out.
 * @param {boolean} isDev - A boolean representing dev mode.
 */
export function devLog(isDev, ...args) {
  if (isDev) {
	console.log(...args);
  }
}

/**
 * Gets a cookie value by name.
 * @param {string} name - The name of the cookie to retrieve.
 * @return {string|null} - The cookie value or null if not found.
 */
export function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}
